import { useEffect, useRef } from 'preact/hooks';
import { Link } from 'wouter-preact';
import { cn } from '@/lib/cn';
import { activeSection } from '@/stores/navigation';

/**
 * Views of the current section, under the top bar. One pattern for every section:
 * the sidebar chooses the section, this bar chooses the view, and each page keeps
 * its own controls (inner tabs, filters) below its header.
 */
export function SectionNav() {
  const section = activeSection.value;
  const tabs = section?.item.tabs;
  const listRef = useRef<HTMLUListElement>(null);
  const activeId = section?.tab?.id;

  // En móvil la barra se desplaza: la vista activa tiene que quedar a la vista.
  useEffect(() => {
    listRef.current
      ?.querySelector('[aria-current="page"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [activeId]);

  if (!section || !tabs || tabs.length < 2) return null;

  return (
    <nav
      aria-label={`Vistas de ${section.item.label}`}
      className="border-border bg-canvas shrink-0 border-b px-4 md:px-8"
    >
      <div className="mx-auto max-w-[var(--layout-max-w)]">
        <ul ref={listRef} className="-mx-3 flex gap-1 overflow-x-auto [scrollbar-width:none]">
          {tabs.map((tab) => {
            const active = activeId === tab.id;
            return (
              <li key={tab.id} className="shrink-0">
                <Link
                  href={tab.path}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'text-13 duration-med relative flex h-11 items-center px-3 font-medium whitespace-nowrap transition-colors outline-none',
                    'focus-visible:shadow-focus focus-visible:ring-brand rounded-sm focus-visible:ring-1',
                    'after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:rounded-full',
                    active
                      ? 'text-fg-strong after:bg-brand'
                      : 'text-fg-muted hover:text-fg-strong after:bg-transparent',
                  )}
                >
                  {tab.label}
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
